<template>
    <jump-transition>
        <div 
            :id="`user-${user.id}`" 
            class="user-wrapper" 
            :class="{ 'scale-up': focus, 'show-pop-animation': staggerPopping }" 
            :style="`--user-index: ${user.index}`"
        >
            <div class="circle" @click="$emit('editUser', user.id)">
                <div class="inner-circle" :class="{ 'has-avatar': user.base64avatar }">
                    <div class="score-wrapper">
                        <div class="first-row row" :class="{ show: user.displayScore >= 100 }" :style="`--translate-y: -${getTranslateY().firstRow}%`">
                            <div class="digits">
                                <span class="digit invisible">0</span>
                                <span class="digit">1</span>
                                <span class="digit">2</span>
                                <span class="digit">3</span>
                                <span class="digit">4</span>
                                <span class="digit">5</span>
                                <span class="digit">6</span>
                                <span class="digit">7</span>
                                <span class="digit">8</span>
                                <span class="digit">9</span>
                            </div>
                        </div>
                        <div class="second-row row" :class="{ show: user.displayScore >= 10 }" :style="`--translate-y: -${getTranslateY().secondRow}%`">
                            <div class="digits">
                                <span class="digit" :class="{ invisible: user.displayScore < 100 }">0</span>
                                <span class="digit">1</span>
                                <span class="digit">2</span>
                                <span class="digit">3</span>
                                <span class="digit">4</span>
                                <span class="digit">5</span>
                                <span class="digit">6</span>
                                <span class="digit">7</span>
                                <span class="digit">8</span>
                                <span class="digit">9</span>
                            </div>
                        </div>
                        <div class="third-row row show" :style="`--translate-y: -${getTranslateY().thirdRow}%`">
                            <div class="digits">
                                <span class="digit">0</span>
                                <span class="digit">1</span>
                                <span class="digit">2</span>
                                <span class="digit">3</span>
                                <span class="digit">4</span>
                                <span class="digit">5</span>
                                <span class="digit">6</span>
                                <span class="digit">7</span>
                                <span class="digit">8</span>
                                <span class="digit">9</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div :id="`outer-circle-${user.id}`" class="outer-circle"></div>
                <avatar class="user-avatar" :userId="user.id" :avatar="user.base64avatar" :fallback="false"></avatar>
            </div>
            <p v-parse-emojis class="name">{{ user.name }}</p>
            <jump-transition jump="big" stagger>
                <div v-if="roundScore && roundScore.shots > 0" class="shot-tag tag jump-stagger">
                    <svg class="shot-icon" width="13" height="13" viewBox="0 0 33 39" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.88889 0.551331C1.72776 1.0611 0.538303 2.50543 0.340061 3.63824C0.22678 4.23297 1.16135 9.84039 3.11545 20.0357C6.34397 36.773 6.37229 36.9146 8.29807 37.7076C9.0344 38.0191 10.9885 38.1041 16.6809 38.1041C24.9504 38.1041 25.5168 37.9908 26.7063 36.2066C27.2444 35.4136 28.5188 29.8062 28.5188 28.2769C28.5188 27.4556 27.3293 26.4927 26.3381 26.4927C25.0071 26.4927 24.4973 27.314 23.8459 30.6841L23.2512 33.7144L16.6809 33.7994L10.1106 33.856L9.96897 33.233C9.68577 31.9302 6.42893 14.6832 6.42893 14.3433C6.42893 14.1168 9.14768 14.0318 16.6242 14.0318H26.8196V14.6548C26.7912 15.023 26.6213 16.1841 26.4231 17.2886C25.885 20.0074 26.4514 21.1968 28.2639 21.1968C29.8781 21.1968 30.0764 20.7154 31.634 12.4742C33.1916 4.43121 33.2483 3.32672 32.2287 1.91071C31.011 0.183167 30.6711 0.154846 16.5393 0.154846C6.54221 0.183167 3.54026 0.268127 2.88889 0.551331ZM28.6887 5.2525C28.6037 5.73395 28.3772 6.9234 28.1789 7.94293L27.8674 9.78375L16.6526 9.72711L5.43772 9.64215L5.09787 7.65973C4.89963 6.55524 4.67307 5.3941 4.58811 5.02594L4.44651 4.40289H16.6526H28.8869L28.6887 5.2525Z" fill="currentColor"/></svg>
                    <span v-if="roundScore.shots > 1">x{{ roundScore.shots }}</span>
                </div>
            </jump-transition>
            <jump-transition jump="big" stagger>
                <div v-if="roundScore && roundScore.winner && roundScore.score === 0" class="win-tag tag jump-stagger">
                    <svg class="fire-icon" width="14" height="14" viewBox="0 0 512 512"><path d="M112 320c0-93 124-165 96-272 66 0 192 96 192 272a144 144 0 01-288 0z" fill="#ff4d00" stroke="#ff4d00" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"/><path d="M320 368c0 57.71-32 80-64 80s-64-22.29-64-80 40-86 32-128c42 0 96 70.29 96 128z" fill="#ff8400" stroke="#ff8400" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"/></svg>
                </div>
            </jump-transition>
            <jump-transition jump="big" stagger>
                <div v-if="roundScore && roundScore.impaled" class="impale-tag tag jump-stagger">
                    <svg class="skull-icon" width="14" height="14" viewBox="0 0 512 512"><path d="M448 225.64v99a64 64 0 01-40.23 59.42l-23.68 9.47A32 32 0 00364.6 417l-10 50.14A16 16 0 01338.88 480H173.12a16 16 0 01-15.69-12.86L147.4 417a32 32 0 00-19.49-23.44l-23.68-9.47A64 64 0 0164 324.67V224c0-105.92 85.77-191.81 191.65-192S448 119.85 448 225.64z" fill="none" stroke="currentColor" stroke-linecap="round" stroke-miterlimit="10" stroke-width="32"/><circle cx="168" cy="280" r="40" fill="none" stroke="currentColor" stroke-linecap="round" stroke-miterlimit="10" stroke-width="32"/><circle cx="344" cy="280" r="40" fill="none" stroke="currentColor" stroke-linecap="round" stroke-miterlimit="10" stroke-width="32"/><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32" d="M256 336l-16 48h32l-16-48zM256 448v32M208 448v32M304 448v32"/></svg>
                </div>
            </jump-transition>
            <jump-transition jump="big" stagger>
                <div v-if="roundScore && roundScore.reset" class="reset-tag tag jump-stagger">
                    <svg class="reset-icon" width="14" height="14" viewBox="0 0 512 512"><path d="M320 146s24.36-12-64-12a160 160 0 10160 160" fill="none" stroke="currentColor" stroke-linecap="round" stroke-miterlimit="10" stroke-width="48"/><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="48" d="M256 58l80 80-80 80"/></svg>
                </div>
            </jump-transition>
            <jump-transition jump="big" stagger>
                <div v-if="roundScore && roundScore.score !== 0 && !roundScore.impaled && !roundScore.reset" class="round-tag tag jump-stagger" :class="{ green: roundScore.score < 0 }">
                    <span v-if="roundScore.score > 0">+</span>
                    <span>{{ roundScore.score }}</span>
                </div>
            </jump-transition>
        </div>
    </jump-transition>
</template>

<script>
import JumpTransition from '@/components/transitions/JumpTransition.vue';
import Avatar from '@/components/Avatar.vue';

export default {
    name: 'user-circle',
    components: {
        JumpTransition,
        Avatar
    },
    props: {
        user: {
            type: Object,
            required: true
        },
        roundScore: {
            type: Object,
            default: null
        },
        focus: {
            type: Boolean,
            default: false
        },
        staggerPopping: {
            type: Boolean,
            default: false
        },
        index: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
        }
    },
    methods: {
        getTranslateY() {
            const firstRow = Math.floor(this.user.displayScore / 100) * 10;
            const secondRow = Math.floor(this.user.displayScore % 100 / 10) * 10;
            const thirdRow = Math.floor(this.user.displayScore % 100 % 10) * 10;

            return {
                firstRow,
                secondRow,
                thirdRow,
            }
        },
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/variables.scss';

.user-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: .3s cubic-bezier(.7,.05,.31,.95);
    -webkit-transition: .3s cubic-bezier(.7,.05,.31,.95);

    &.enable-drag.show-pop-animation {
        --animation-delay: calc(var(--user-index, 0) * 125ms);
        animation: pop-circle 4s ease infinite var(--animation-delay);
    }
    
    &.scale-up {
        transition: .3s cubic-bezier(.51,2.5,.45,1.08) .1s !important;
        -webkit-transition: .3s cubic-bezier(.51,2.5,.45,1.08) .1s !important;
        transform: scale(1.15);
        -webkit-transform: scale(1.15);
    }
    .circle {
        position: relative;
        --width-outer-circle: 8px;
        --gap-outer-circle: calc(var(--absolute-width) / 10);
        --circle-size: min(calc(42px + (var(--absolute-width) / 10)), 100px);
        position: relative;
        width: var(--circle-size);
        aspect-ratio: 1/1;
        padding: calc((var(--gap-outer-circle) + var(--width-outer-circle)) / 2);
        isolation: isolate;
        user-select: none;

        .inner-circle {
            position: relative;
            background-color: $weed-green;
            border-radius: 50%;
            width: calc(var(--circle-size));
            aspect-ratio: 1/1;
            display: grid;
            place-items: center;
            box-shadow: 0 0 0 calc(var(--gap-outer-circle) / 2) #204923;
            transition: .3s ease-out;

            &.has-avatar {
                background-color: #204923;
            }

            .score-wrapper {
                display: flex;
                color: #f1f1f1;
                font-weight: 500;
                font-size: calc(var(--absolute-width) / 100 * 7.5);
                height: calc(var(--absolute-width) / 100 * 7.5);
                overflow: hidden;
                z-index: 1;
                padding: 1px 3px;
                margin: -1px -3px;

                .row {
                    max-width: 0;
                    opacity: 0;
                    transition: .8s cubic-bezier(.2,1.05,.2,1);
                    overflow: hidden;
                    padding: 1px 3px;
                    margin: -1px -3px;
                    
                    &.show {
                        opacity: 1;
                        max-width: calc(var(--absolute-width) / 100 * 4);
                    }

                    .digits {
                        display: flex;
                        flex-direction: column;
                        translate: 0 var(--translate-y, 0);
                        transition: translate .8s cubic-bezier(.2,1.05,.2,1);

                        .digit {
                            line-height: calc(var(--absolute-width) / 100 * 7.5);
                            text-shadow: 0 0 5px #132914;

                            &.invisible {
                                opacity: 0;
                            }
                        }
                    }

                }
            }
        }
        .outer-circle {
            position: absolute;
            left: 50%;
            top: 50%;
            translate: -50% -50%;

            background: var(--background-style, #335f36);

            border-radius: 50%;
            width: calc(var(--circle-size) + var(--gap-outer-circle) + var(--width-outer-circle));
            aspect-ratio: 1/1;
            z-index: -1;

            mix-blend-mode: lighten;

            &:after {
                content: "";
                position: absolute;
                inset: calc(var(--width-outer-circle) / 2);
                border-radius: 50%;
                background: black;
                mix-blend-mode: multiply;
            }
        }

        .user-avatar {
            position: absolute;
            top: calc(var(--width-outer-circle) / 2);
            left: calc(var(--width-outer-circle) / 2);
            width: calc(var(--circle-size) + var(--gap-outer-circle));
            height: calc(var(--circle-size) + var(--gap-outer-circle));
            border-radius: 50%;
            overflow: hidden;
        }
    }
    
    .name {
        color: #f1f1f1;
        margin-top: 12px;
        width: fit-content;
        text-align: center;
    }

    .tag {
        position: absolute;
        bottom: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
        background: #606d60ae;
        border-radius: 16px;
        height: 32px;
        padding-inline: 10px;
        color: #ffffffee;
        --delay-1: calc(var(--user-index) * 100ms + 1000ms);
        --delay-2: calc(var(--user-index) * 100ms + 3000ms);
    }

    .shot-tag {
        left: 14px;
        font-weight: 500;
        font-size: 15px;
        display: flex;
        gap: 4px;

        .shot-icon {
            color: #ffffffee;
            transform-origin: 70% 35%;
            scale: 1.1;
            animation: tease-shot 4s cubic-bezier(.7,.05,.31,.95) infinite var(--delay-1);
        }
    }

    .win-tag {
        right: 14px;

        .fire-icon {
            scale: 1.4;
            opacity: .9;
        }
    }

    .impale-tag {
        right: 14px;

        .skull-icon {
            scale: 1.35;
            color: #ffffffee;
            opacity: .9;
        }
    }

    .reset-tag {
        right: 14px;

        .reset-icon {
            scale: 1.35;
            color: lighten($weed-green, 10%);
            transform: scaleX(-1);
            transform-origin: 50% 57%;
            animation: rotate-reset 4s cubic-bezier(.7,.05,.31,.95) infinite var(--delay-2);
            opacity: .9;
        }
    }

    .round-tag {
        right: 14px;
        font-weight: 500;
        font-size: 17px;

        &.green {
            color: lighten($weed-green, 10%);
        }
    }
}

@keyframes tease-shot {
    0%, 60%, 100% {
        transform: rotate(0deg);
    } 15% {
        transform: rotate(35deg);
    }
}
@keyframes rotate-reset {
    0% {
        rotate: 0deg;
    } 25%, 100% {
        rotate: -360deg;
    }
}
@keyframes pop-circle {
    0%, 40%, 60%, 100% {
        scale: 1;
    } 50% {
        scale: 1.1;
    }
}

</style>